import { STATUS_ORDER } from "../computeQuantityDiff";
import type {
  VisualDiffStatus,
  VisualDiffStatusCounts,
  VisualDiffTypeEntry,
} from "./types";

export const buildEmptyStatusRecord = <TItem>(): Record<
  VisualDiffStatus,
  TItem[]
> => ({
  added: [],
  removed: [],
  changed: [],
  unchanged: [],
});

export const countStatuses = (
  items: { status: VisualDiffStatus }[],
): VisualDiffStatusCounts => {
  const counts: VisualDiffStatusCounts = {
    addedCount: 0,
    removedCount: 0,
    changedCount: 0,
    unchangedCount: 0,
  };
  for (const item of items) {
    if (item.status === "added") counts.addedCount += 1;
    if (item.status === "removed") counts.removedCount += 1;
    if (item.status === "changed") counts.changedCount += 1;
    if (item.status === "unchanged") counts.unchangedCount += 1;
  }
  return counts;
};

export const hasChangedItems = (counts: VisualDiffStatusCounts): boolean =>
  counts.addedCount + counts.removedCount + counts.changedCount > 0;

// Driven by what each side actually holds: a plan can carry a type whose items
// are all gone, and that reads as removed, not changed.
export const resolveTypeStatus = (
  beforeItemCount: number,
  afterItemCount: number,
  counts: VisualDiffStatusCounts,
): VisualDiffStatus => {
  if (!hasChangedItems(counts)) return "unchanged";
  if (beforeItemCount === 0) return "added";
  if (afterItemCount === 0) return "removed";
  return "changed";
};

// A type with nothing changed still needs a size to sort by, so it falls back to what it holds.
const entryMagnitude = (entry: VisualDiffTypeEntry): number => {
  if (entry.status === "unchanged") return entry.unchangedCount;
  if (entry.metersDelta !== null) return Math.abs(entry.metersDelta);
  return entry.addedCount + entry.removedCount + entry.changedCount;
};

export const compareTypeEntries = (
  firstEntry: VisualDiffTypeEntry,
  secondEntry: VisualDiffTypeEntry,
): number => {
  if (firstEntry.kind !== secondEntry.kind) {
    return firstEntry.kind === "elements" ? -1 : 1;
  }
  const statusDifference =
    STATUS_ORDER[firstEntry.status] - STATUS_ORDER[secondEntry.status];
  if (statusDifference !== 0) return statusDifference;
  return entryMagnitude(secondEntry) - entryMagnitude(firstEntry);
};
